import {
    TableCell,
    TableRow,
    Theme,
    Typography
} from "@mui/material";
import { HoldingDetailsTableData } from "../types";
import { formatCurrency } from "../../../../utils/utilityFunctions";
import { useSelector } from "react-redux";
import { RootStateType } from "../../../../redux-store/reducers";

export default function DataTable({
    data,
    headerData
}: {
    data: HoldingDetailsTableData[],
    headerData: string[]
}
): JSX.Element {
    const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);

    return (
        <>
            {
                data.map((obj: HoldingDetailsTableData) => {
                    return (
                        <TableRow
                            key={obj.id}
                            sx={(theme: Theme) => ({
                                '& td': {
                                    paddingTop: "15px",
                                    paddingBottom: "15px",
                                    borderBottom: `0.5px solid ${theme.palette.text.secondary}B2`,
                                },
                                '& td:first-of-type': {
                                    paddingLeft: "0px"
                                },
                            })}
                        >
                            {headerData.map((currentHeader: string) => {
                                const percentageDataColor = obj[currentHeader]?.amount >= 0 ? 'success.800' : 'error.500'

                                return (
                                    <TableCell
                                        key={currentHeader}
                                        align={currentHeader === "schemeName" ? "left" : "center"}
                                        sx={(theme: Theme) => ({
                                            ...(currentHeader === "schemeName" ? {
                                                position: "sticky",
                                                left: 0,
                                                background: theme.palette.common.white,
                                                zIndex: 1
                                            } : {})
                                        })}
                                    >
                                        <Typography
                                            variant="body_500"
                                            sx={(theme: Theme) => ({
                                                lineHeight: "20px",
                                                letterSpacing: "0.02px",
                                                color: currentHeader === "percentage" ? percentageDataColor : theme.palette.text.primary,
                                                textTransform: "none",
                                                ":hover": {
                                                    color: currentHeader === "schemeName" ? theme.palette.primary[500] : currentHeader === "percentage" ? percentageDataColor : theme.palette.text.primary
                                                }
                                            })}
                                        >
                                            {currentHeader === "schemeName" && (
                                                obj[currentHeader] ?? "-"
                                            )}
                                            {currentHeader === "units" && (
                                                obj[currentHeader] ?? "-"
                                            )}
                                            {currentHeader === "shares1Year" && (
                                                obj[currentHeader] ?? "-"
                                            )}
                                            {currentHeader === "avgCostPrice" && (
                                                `${formatCurrency({
                                                    value: !Number.isNaN(Number(obj[currentHeader] ?? 0)) ?
                                                        Math.round(Number(obj[currentHeader] ?? 0) * 100) / 100 : 0,
                                                    locale,
                                                    currencyCode
                                                })}`
                                            )}
                                            {currentHeader === "percentage" && (
                                                ((obj[currentHeader]?.amount ?? 0) < 0 ?
                                                    `- ${formatCurrency({
                                                        value: Math.abs(obj[currentHeader]?.amount ?? 0),
                                                        locale,
                                                        currencyCode
                                                    })} (${!Number.isNaN(Number(obj[currentHeader]?.percentage ?? 0)) ?
                                                        Math.round((obj[currentHeader]?.percentage ?? 0) * 100) / 100 : '0'}%)`
                                                    :
                                                    `${formatCurrency({ value: obj[currentHeader]?.amount ?? 0, locale, currencyCode })} (${!Number.isNaN(Number(obj[currentHeader]?.percentage ?? 0)) ?
                                                        Math.round((obj[currentHeader]?.percentage ?? 0) * 100) / 100 : '0'}%)`)
                                            )}
                                            {currentHeader === "exposure" && (
                                                !Number.isNaN(Number(obj[currentHeader] ?? 0)) ?
                                                    Math.round((obj[currentHeader] ?? 0) * 100) / 100 : '0'
                                            )}
                                            {currentHeader === "investedValue" && (
                                                `${formatCurrency({
                                                    value: !Number.isNaN(Number(obj[currentHeader] ?? 0)) ?
                                                        Math.round(Number(obj[currentHeader] ?? 0) * 100) / 100 : 0,
                                                    locale,
                                                    currencyCode
                                                })}`
                                            )}
                                            {currentHeader === "marketValue" && (
                                                `${formatCurrency({
                                                    value: !Number.isNaN(Number(obj[currentHeader] ?? 0)) ?
                                                        Math.round(Number(obj[currentHeader] ?? 0) * 100) / 100 : 0,
                                                    locale,
                                                    currencyCode
                                                })}`
                                            )}
                                            {currentHeader === "week52High" && (
                                                `${formatCurrency({
                                                    value: !Number.isNaN(Number(obj[currentHeader] ?? 0)) ?
                                                        Math.round(Number(obj[currentHeader] ?? 0) * 100) / 100 : 0,
                                                    locale,
                                                    currencyCode
                                                })}`
                                            )}
                                            {currentHeader === "week52Low" && (
                                                `${formatCurrency({
                                                    value: !Number.isNaN(Number(obj[currentHeader] ?? 0)) ?
                                                        Math.round(Number(obj[currentHeader] ?? 0) * 100) / 100 : 0,
                                                    locale,
                                                    currencyCode
                                                })}`
                                            )}
                                            {currentHeader === "currentPrice" && (
                                                `${formatCurrency({
                                                    value: !Number.isNaN(Number(obj[currentHeader] ?? 0)) ?
                                                        Math.round(Number(obj[currentHeader] ?? 0) * 100) / 100 : 0,
                                                    locale,
                                                    currencyCode
                                                })}`
                                            )}
                                            {currentHeader === "eps" && (
                                                obj[currentHeader] ?? "-"
                                            )}
                                            {currentHeader === "pe" && (
                                                obj[currentHeader] ?? "-"
                                            )}
                                            {currentHeader === "pb" && (
                                                obj[currentHeader] ?? "-"
                                            )}
                                            {currentHeader === "marketCapitalisation" && (
                                                obj[currentHeader] ?? "-"
                                            )}
                                            {currentHeader === "dividend" && (
                                                !Number.isNaN(Number(obj[currentHeader] ?? 0)) ?
                                                    Math.round((obj[currentHeader] ?? 0) * 100) / 100 : '0'
                                            )}
                                            {currentHeader === "sectorPE" && (
                                                obj[currentHeader] ?? "-"
                                            )}
                                        </Typography>
                                    </TableCell>
                                )
                            })}
                        </TableRow >
                    )
                })
            }
        </>
    )
}